"use client";

import React from "react";
import { PRACTICE_AREAS, PracticeAreaInfo } from "@/lib/demos/legalData";
import { 
  Briefcase, 
  ShieldAlert, 
  Building2, 
  Lightbulb, 
  Scale, 
  HeartHandshake, 
  ArrowRight,
  CheckCircle2
} from "lucide-react";

interface PracticeAreasSectionProps {
  currency: "INR" | "USD";
  onSelectPracticeArea: (area: PracticeAreaInfo) => void;
}

const PRACTICE_ICONS: Record<string, any> = {
  "corporate-ma": Briefcase,
  "criminal-bail": ShieldAlert,
  "civil-property": Building2,
  "intellectual-property": Lightbulb,
  "arbitration-adr": Scale,
  "family-trusts": HeartHandshake,
};

export default function PracticeAreasSection({
  currency,
  onSelectPracticeArea,
}: PracticeAreasSectionProps) {
  return (
    <section id="practice-areas" className="py-16 md:py-24 bg-slate-950 border-b border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14 space-y-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-900 border border-amber-500/20 text-amber-400 text-xs font-semibold">
            <span>Factual Practice Directory</span>
          </div>
          <h2 className="text-2xl sm:text-4xl font-serif font-bold text-white tracking-tight">
            Specialized Practice Areas & Legal Domains
          </h2>
          <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
            Direct advocate consultation and trial representation across Indian High Courts, the Supreme Court, statutory tribunals, and commercial arbitration institutions.
          </p>
        </div>

        {/* 6 Practice Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PRACTICE_AREAS.map((area) => {
            const Icon = PRACTICE_ICONS[area.id] || Scale;
            const retainerAmount = currency === "USD" ? area.indicativeRetainerUSD : area.indicativeRetainerINR;
            const formattedRetainer = currency === "USD" 
              ? `$${retainerAmount.toLocaleString()}` 
              : `₹${retainerAmount.toLocaleString("en-IN")}`;

            return (
              <div
                key={area.id}
                className="group relative p-6 rounded-2xl bg-gradient-to-b from-slate-900/90 to-slate-950/90 border border-slate-800 hover:border-amber-500/40 transition-all duration-300 hover:shadow-xl hover:shadow-amber-500/5 flex flex-col justify-between"
              >
                <div>
                  {/* Icon & Category Tag */}
                  <div className="flex items-center justify-between gap-2 mb-4">
                    <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 group-hover:scale-110 transition-transform">
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-slate-850 text-slate-400 border border-slate-750">
                      {area.category}
                    </span>
                  </div>

                  {/* Title & Description */}
                  <h3 className="text-base font-serif font-bold text-white group-hover:text-amber-300 transition-colors mb-2 line-clamp-2">
                    {area.title}
                  </h3>
                  <p className="text-xs text-slate-400 leading-relaxed mb-4">
                    {area.description}
                  </p>

                  {/* Statutory Focus */}
                  <div className="text-[11px] font-semibold text-amber-400/90 mb-4 pb-3 border-b border-slate-800/80">
                    ⚖️ {area.statutoryFocus}
                  </div>

                  {/* Typical Matters Handled */}
                  <div className="space-y-1.5 mb-6">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      Core Briefs & Filings:
                    </span>
                    {area.typicalMatters.map((matter, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs text-slate-300">
                        <CheckCircle2 className="w-3.5 h-3.5 text-amber-500/70 shrink-0" />
                        <span className="line-clamp-1">{matter}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Footer: Indicative Retainer & Intake Action */}
                <div className="pt-4 border-t border-slate-800 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[10px] text-slate-500">Initial Retainer Estimate:</div>
                    <div className="text-sm font-bold text-white font-mono">
                      {formattedRetainer}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => onSelectPracticeArea(area)}
                    className="px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-amber-500 text-slate-300 hover:text-slate-950 font-bold text-xs transition-all flex items-center gap-1.5"
                  >
                    <span>Consult</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
}
